import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
// Client-side data layer for Connectors (third-party Composio integrations:
// Gmail, Google Calendar, Outlook, Slack, …). Mirrors lib/widgets.ts: hit
// `${API_BASE}/api/connectors/*` with credentials. The API routes resolve the
// caller's metasite from their identity.


/** Backing system for a connector row. Omitted/`composio` = a dynamic Composio connector.
 *  `native` = the calendar rows backed by our own tokens; `mcp` = a first-party MCP connector
 *  (constants registry) whose connection lives on the MCP rail (McpConnectionService). */
export type ConnectorProvider = 'composio' | 'native' | 'mcp';

/** A connected account on a connector row — a Composio connected-account id, or a native calendar token
 *  id. `label` is its email/handle when known (else the client shows an ordinal fallback);
 *  `secondaryLabel` is the optional human/workspace line beneath it ("Work, Pizza Milano" / "Personal").
 *  Mirrors native's `ConnectorAccount` — the field is server-optional and only some connectors carry it,
 *  so render it only when present. */
export interface ConnectorAccount {
  id: string;
  label?: string;
  secondaryLabel?: string;
}

export interface ConnectorStatus {
  toolkit: string;
  name?: string;
  logo?: string;
  connected: boolean;
  /** Backing system. Omitted = `composio` (the historical default). */
  provider?: ConnectorProvider;
  /** Native calendar provider (`GOOGLE` | `MICROSOFT` | `ICLOUD`) for a `native` calendar row. */
  calendarProvider?: string;
  /** Connected native accounts (calendar token ids + emails) — what the client deletes on disconnect. */
  accounts?: ConnectorAccount[];
  /** Server-provided (Composio) English blurb. The client prefers a localized `connectors.desc.*` key
   *  (see `connectorDescriptionKey`) and only falls back to this. Mirrors native `connectorDescription`. */
  description?: string;
}

// The connected-row account-list rule lives in its own import-free module so it can be unit-tested; it is
// re-exported here so call sites keep importing it from `lib/connectors`. Mirrored 1:1 on iOS + Android.
export { showsAccountList, supportsMultipleAccounts } from './connector-account-list';

/** Composio's hosted logo for a toolkit — same source the mobile rows use. */
export function connectorLogoUrl(toolkit: string): string {
  return `https://logos.composio.dev/api/${toolkit}`;
}

/** Curated label overrides; otherwise the server-provided name (or slug). Mirrors mobile's connector-row. */
const CONNECTOR_LABELS: Record<string, string> = {
  gmail: 'Gmail',
};

export function connectorDisplayName(connector: ConnectorStatus): string {
  return CONNECTOR_LABELS[connector.toolkit] ?? connector.name ?? connector.toolkit;
}

/** Localized-description i18n key for a KNOWN connector, else null (unknown toolkit → fall back to the
 *  server `description`, else hide the line). Mirrors native `ConnectorsScreen.descriptionKey`: native
 *  calendar rows map by `calendarProvider`; Composio rows use `connectors.desc.<toolkit-slug>`. */
export function connectorDescriptionKey(connector: ConnectorStatus): string | null {
  if (connector.provider === 'native') {
    switch ((connector.calendarProvider ?? '').toUpperCase()) {
      case 'GOOGLE':
        return 'connectors.desc.calendar.google';
      case 'MICROSOFT':
      case 'OUTLOOK':
        return 'connectors.desc.calendar.microsoft';
      case 'ICLOUD':
      case 'APPLE':
        return 'connectors.desc.calendar.apple';
      default:
        return null;
    }
  }
  const slug = connector.toolkit.toLowerCase();
  return slug ? `connectors.desc.${slug}` : null;
}

export interface ConnectorsStatus {
  /** False when the connectors feature isn't configured (no Composio key) — distinct from "none available". */
  enabled: boolean;
  connectors: ConnectorStatus[];
  /** Whether the multi-account rollout is on for this caller — gates the per-account UI + "Add another". */
  multiAccountEnabled: boolean;
}

export async function fetchConnectors(): Promise<ConnectorsStatus> {
  const res = await apiFetch(`${API_BASE}/api/connectors/status`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  const data = (await res.json()) as { enabled?: boolean; connectors?: ConnectorStatus[]; multiAccountEnabled?: boolean };
  return { enabled: data.enabled ?? false, connectors: data.connectors ?? [], multiAccountEnabled: data.multiAccountEnabled ?? false };
}

export interface AuthorizeResult {
  url: string | null;
  /** True when the toolkit already has an ACTIVE connection — refresh status instead of erroring. */
  alreadyConnected?: boolean;
  error?: string;
}

// `allowMultiple` is the explicit "add another account" intent — links an additional account on an
// already-connected toolkit (honored server-side only when the multi-account rollout is on).
export async function authorizeConnector(toolkit: string, allowMultiple = false): Promise<AuthorizeResult> {
  const query = `?toolkit=${encodeURIComponent(toolkit)}${allowMultiple ? '&allowMultiple=true' : ''}`;
  const res = await apiFetch(`${API_BASE}/api/connectors/authorize${query}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  return (await res.json()) as AuthorizeResult;
}

// `accountId` scopes the disconnect to ONE Composio account of a multiply-linked toolkit; omitted, the
// whole connector is disconnected (every account). Native calendar rows use `disconnectCalendarAccounts`.
export async function disconnectConnector(toolkit: string, accountId?: string): Promise<void> {
  const query = `?toolkit=${encodeURIComponent(toolkit)}${accountId ? `&accountId=${encodeURIComponent(accountId)}` : ''}`;
  const res = await apiFetch(`${API_BASE}/api/connectors/disconnect${query}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
}

/**
 * Disconnect a NATIVE calendar row by deleting its backing calendar token(s) — the same `/api/calendars`
 * DELETE the settings Calendars section uses. A native row carries one token id per connected account.
 */
export async function disconnectCalendarAccounts(accountIds: string[]): Promise<void> {
  for (const id of accountIds) {
    const res = await apiFetch(`${API_BASE}/api/calendars?id=${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      throw new Error(await readErrorMessage(res));
    }
  }
}

/** Map the server's string `calendarProvider` to the web calendar catalog's ProviderId. */
export function calendarProviderToProviderId(calendarProvider?: string): 'google' | 'microsoft' | 'apple' | null {
  return calendarProvider === 'GOOGLE'
    ? 'google'
    : calendarProvider === 'MICROSOFT'
    ? 'microsoft'
    : calendarProvider === 'ICLOUD'
    ? 'apple'
    : null;
}

async function readErrorMessage(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string; message?: string };
    return data.error || data.message || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}
